// src/pages/supervisor/SupervisorTopicView.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fade,
    Zoom,
    Card,
    CardContent,
    LinearProgress,
    Avatar,
    Stack,
    Badge,
    Tabs,
    Tab,
    Rating,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel,
    Checkbox
} from '@mui/material';
import {
    Search,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Today as TodayIcon,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Assignment,
    Psychology,
    MenuBook,
    Science,
    QuestionAnswer,
    Flag,
    Timeline,
    RateReview,
    Feedback,
    Approval,
    Verified,
    Pending,
    Article,
    Description,
    LocalOffer,
    Lightbulb,
    School,
    SupervisorAccount,
    DateRange,
    Update,
    ExpandMore,
    ClearAll,
    ThumbUp,
    ThumbDown,
    Comment,
    Send,
    Assessment,
    TrendingUp,
    EmojiEvents,
    Star,
    StarBorder,
    TaskAlt,
    RemoveCircle,
    CheckCircleOutline,
    CancelOutlined,
    Category,
    AccountBalance
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchTopicUpdateStatus,
    APIResearchTopicBySupervisor,
    APIResearchTopicMarkCompleted
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';
// Topic status options
const topicStatusOptions = [
    { id: 'draft', name: 'Draft', color: 'default', icon: <Description /> },
    { id: 'submitted', name: 'Submitted', color: 'info', icon: <Send /> },
    { id: 'under_review', name: 'Under Review', color: 'warning', icon: <Schedule /> },
    { id: 'revision_required', name: 'Revision Required', color: 'warning', icon: <RateReview /> },
    { id: 'approved', name: 'Approved', color: 'success', icon: <CheckCircle /> },
    { id: 'rejected', name: 'Rejected', color: 'error', icon: <Cancel /> },
    { id: 'completed', name: 'Completed', color: 'success', icon: <TaskAlt /> },
    { id: 'superseded', name: 'Superseded', color: 'secondary', icon: <Update /> }
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SupervisorTopicView = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [topics, setTopics] = useState([]);
    const [filteredTopics, setFilteredTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openApproveDialog, setOpenApproveDialog] = useState(false);
    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
    const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
    const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
    const [feedbackInput, setFeedbackInput] = useState('');
    const [approvalNotes, setApprovalNotes] = useState('');
    const [completionNotes, setCompletionNotes] = useState('');
    const [filterOptions, setFilterOptions] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        pending_review: 0,
        under_review: 0,
        approved: 0,
        rejected: 0,
        revision_required: 0,
        completed: 0
    });
    
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    // Filters
    const [filters, setFilters] = useState({
        topic_status: '',
        student_name: '',
        specialty_id: '',
        university_id: '',
        dateFrom: '',
        dateTo: ''
    });

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        
        // Auto-refresh every 2 minutes
        const interval = setInterval(() => {
            fetchData(true);
        }, 120000);
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        applyFiltersAndSort();
    }, [topics, searchTerm, filters, activeTab]);

    const fetchData = async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);

        try {
            // Fetch all research topics for supervisor's students
            const result = await apiService.listById(APIResearchTopicBySupervisor, supervisorId);

            if (result.success && isMounted.current) {
                let topicsData = Array.isArray(result.data) ? result.data : [];

                setTopics(topicsData);
                calculateStats(topicsData);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current && !silent) {
                setLoading(false);
            }
        }
    };

    const calculateStats = (topicsData) => {
        const newStats = {
            total: topicsData.length,
            pending_review: topicsData.filter(t => t.topic_status === 'submitted').length,
            under_review: topicsData.filter(t => t.topic_status === 'under_review').length,
            approved: topicsData.filter(t => t.topic_status === 'approved').length,
            rejected: topicsData.filter(t => t.topic_status === 'rejected').length,
            revision_required: topicsData.filter(t => t.topic_status === 'revision_required').length,
            completed: topicsData.filter(t => t.topic_status === 'completed').length
        };
        setStats(newStats);
    };

    // Get unique specialties from topics for filter dropdown
    const getUniqueSpecialties = () => {
        const specialties = new Map();
        topics.forEach(topic => {
            if (topic.specialty_id && topic.specialty_name) {
                specialties.set(topic.specialty_id, {
                    id: topic.specialty_id,
                    name: topic.specialty_name,
                    code: topic.specialty_code
                });
            }
        });
        return Array.from(specialties.values());
    };

    // Get unique universities from topics for filter dropdown
    const getUniqueUniversities = () => {
        const universities = new Map();
        topics.forEach(topic => {
            if (topic.university_id && topic.university_name) {
                universities.set(topic.university_id, {
                    id: topic.university_id,
                    name: topic.university_name
                });
            }
        });
        return Array.from(universities.values());
    };

    const applyFiltersAndSort = () => {
        let filtered = [...topics];
        
        // Filter by tab
        if (activeTab === 1) filtered = filtered.filter(t => t.topic_status === 'submitted');
        else if (activeTab === 2) filtered = filtered.filter(t => t.topic_status === 'under_review');
        else if (activeTab === 3) filtered = filtered.filter(t => t.topic_status === 'revision_required');
        else if (activeTab === 4) filtered = filtered.filter(t => t.topic_status === 'approved');
        else if (activeTab === 5) filtered = filtered.filter(t => t.topic_status === 'rejected');
        else if (activeTab === 6) filtered = filtered.filter(t => t.topic_status === 'completed');
        
        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t => 
                t.topic_title?.toLowerCase().includes(term) ||
                t.topic_id?.toLowerCase().includes(term) ||
                t.student_name?.toLowerCase().includes(term) ||
                t.student_registration_id?.toLowerCase().includes(term) ||
                t.specialty_name?.toLowerCase().includes(term) ||
                t.university_name?.toLowerCase().includes(term)
            );
        }
        
        // Filter by status dropdown
        if (filters.topic_status) {
            filtered = filtered.filter(t => t.topic_status === filters.topic_status);
        }
        
        // Filter by student name
        if (filters.student_name) {
            filtered = filtered.filter(t => 
                t.student_name?.toLowerCase().includes(filters.student_name.toLowerCase())
            );
        }
        
        // Filter by specialty
        if (filters.specialty_id) {
            filtered = filtered.filter(t => t.specialty_id === parseInt(filters.specialty_id));
        }
        
        // Filter by university
        if (filters.university_id) {
            filtered = filtered.filter(t => t.university_id === parseInt(filters.university_id));
        }
        
        // Filter by date range
        if (filters.dateFrom) {
            filtered = filtered.filter(t => new Date(t.submitted_date) >= new Date(filters.dateFrom));
        }
        if (filters.dateTo) {
            filtered = filtered.filter(t => new Date(t.submitted_date) <= new Date(filters.dateTo));
        }
        
        setFilteredTopics(filtered);
        setPage(0);
    };

    const handleOpenViewDialog = (topic) => {
        setSelectedTopic(topic);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedTopic(null);
    };

    const handleOpenApproveDialog = (topic) => {
        setSelectedTopic(topic);
        setApprovalNotes('');
        setOpenApproveDialog(true);
    };

    const handleCloseApproveDialog = () => {
        setOpenApproveDialog(false);
        setSelectedTopic(null);
        setApprovalNotes('');
    };

    const handleOpenRejectDialog = (topic) => {
        setSelectedTopic(topic);
        setFeedbackInput('');
        setOpenRejectDialog(true);
    };

    const handleCloseRejectDialog = () => {
        setOpenRejectDialog(false);
        setSelectedTopic(null);
        setFeedbackInput('');
    };

    const handleOpenCompleteDialog = (topic) => {
        setSelectedTopic(topic);
        setCompletionNotes('');
        setOpenCompleteDialog(true);
    };

    const handleCloseCompleteDialog = () => {
        setOpenCompleteDialog(false);
        setSelectedTopic(null);
        setCompletionNotes('');
    };

    const handleOpenFeedbackDialog = (topic) => {
        setSelectedTopic(topic);
        setFeedbackInput(topic.supervisor_feedback || '');
        setOpenFeedbackDialog(true);
    };

    const handleCloseFeedbackDialog = () => {
        setOpenFeedbackDialog(false);
        setSelectedTopic(null);
        setFeedbackInput('');
    };

    const handleUpdateStatus = async (topicId, newStatus, feedback = null, additionalNotes = null) => {
        setStatusUpdateLoading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', topicId);
            formDataToSend.append('topic_status', newStatus);
            
            if (feedback) {
                formDataToSend.append('supervisor_feedback', feedback);
            }
            
            if (additionalNotes) {
                formDataToSend.append('reviewer_comments', additionalNotes);
            }

            const result = await apiService.update(APIResearchTopicUpdateStatus, formDataToSend);

            if (result && result.success && isMounted.current) {
                const statusName = topicStatusOptions.find(s => s.id === newStatus)?.name || newStatus;
                showToast("Success", `Topic ${statusName.toLowerCase()} successfully`, "success");
                fetchData();
                
                // Close all dialogs
                handleCloseApproveDialog();
                handleCloseRejectDialog();
                handleCloseCompleteDialog();
                handleCloseFeedbackDialog();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update status", "error");
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    const handleCompletedResearch = async (topicId, newStatus, feedback = null, additionalNotes = null) => {
        setStatusUpdateLoading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', topicId);
            formDataToSend.append('topic_status', newStatus);

            if (feedback) {
                formDataToSend.append('supervisor_feedback', feedback);
            }
            if (additionalNotes) {
                formDataToSend.append('completion_notes', additionalNotes);
            }

            const result = await apiService.update(APIResearchTopicMarkCompleted, formDataToSend);

            if (result && result.success && isMounted.current) {
                const statusName = topicStatusOptions.find(s => s.id === newStatus)?.name || newStatus;
                showToast("Success", `Topic ${statusName.toLowerCase()} successfully`, "success");
                fetchData();
                // Close all dialogs
                handleCloseApproveDialog();
                handleCloseRejectDialog();
                handleCloseCompleteDialog();
                handleCloseFeedbackDialog();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update status", "error");
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            topic_status: '',
            student_name: '',
            specialty_id: '',
            university_id: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setActiveTab(0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusIcon = (status) => {
        const option = topicStatusOptions.find(s => s.id === status);
        return option?.icon || <Schedule />;
    };

    const getStatusColor = (status) => {
        const option = topicStatusOptions.find(s => s.id === status);
        return option?.color || 'default';
    };

    if (loading) {
        return <LoadingState message="Loading Student Research Topics..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Research Topics" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <SupervisorBar notificationCount={5} />
                
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                            <Assessment /> Student Research Topics
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={() => setFilterOptions(true)}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Filter
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={() => fetchData()}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search by topic title, ID, student name, specialty, or university..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Stats Cards */}
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.primary.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.total}</Typography>
                                    <Typography variant="caption">Total</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.pending_review}</Typography>
                                    <Typography variant="caption">Pending</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.info.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.under_review}</Typography>
                                    <Typography variant="caption">Under Review</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.revision_required}</Typography>
                                    <Typography variant="caption">Revision</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.approved}</Typography>
                                    <Typography variant="caption">Approved</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.error.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.rejected}</Typography>
                                    <Typography variant="caption">Rejected</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.7}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light + '20' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.completed}</Typography>
                                    <Typography variant="caption">Completed</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 100 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="All" />
                        <Tab label="Pending Review" />
                        <Tab label="Under Review" />
                        <Tab label="Revision Required" />
                        <Tab label="Approved" />
                        <Tab label="Rejected" />
                        <Tab label="Completed" />
                    </Tabs>
                </Paper>

                {/* Topics Table */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'auto', backgroundColor: 'white' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 700, sm: 900, md: 1100 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': { color: 'white', fontWeight: 'bold' }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Student</TableCell>
                                        <TableCell>Topic Title</TableCell>
                                        <TableCell>University</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Submitted Date</TableCell>
                                        <TableCell>Assigned</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredTopics.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((topic, index) => (
                                        <TableRow key={topic.id} hover>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                                        {topic.student_name?.charAt(0) || 'S'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {topic.student_name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {topic.student_registration_id}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Article fontSize="small" color="primary" />
                                                    <Typography variant="body2" sx={{ 
                                                        maxWidth: 250, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap' 
                                                    }}>
                                                        {topic.topic_title}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
        
                                            <TableCell>
                                                {topic.university_name ? (
                                                    <Chip
                                                        icon={<AccountBalance />}
                                                        label={topic.university_name}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="No University"
                                                        size="small"
                                                        variant="outlined"
                                                        color="default"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(topic.topic_status)}
                                                    label={topicStatusOptions.find(s => s.id === topic.topic_status)?.name || topic.topic_status}
                                                    color={getStatusColor(topic.topic_status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(topic.submitted_date)}</TableCell>
                                            <TableCell>{topic.assigned_date}</TableCell>
                                            <TableCell >
                                                <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                                    <Tooltip title="View Details">
                                                        <IconButton onClick={() => handleOpenViewDialog(topic)} color="primary" size="small">
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    {topic.topic_status === 'submitted' && (
                                                        <>
                                                            <Tooltip title="Approve">
                                                                <IconButton onClick={() => handleOpenApproveDialog(topic)} color="success" size="small">
                                                                    <ThumbUp />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reject">
                                                                <IconButton onClick={() => handleOpenRejectDialog(topic)} color="error" size="small">
                                                                    <ThumbDown />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Request Revision">
                                                                <IconButton onClick={() => handleOpenFeedbackDialog(topic)} color="warning" size="small">
                                                                    <RateReview />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    
                                                    {topic.topic_status === 'under_review' && (
                                                        <>
                                                            <Tooltip title="Approve">
                                                                <IconButton onClick={() => handleOpenApproveDialog(topic)} color="success" size="small">
                                                                    <CheckCircle />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reject">
                                                                <IconButton onClick={() => handleOpenRejectDialog(topic)} color="error" size="small">
                                                                    <Cancel />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Request Revision">
                                                                <IconButton onClick={() => handleOpenFeedbackDialog(topic)} color="warning" size="small">
                                                                    <RateReview />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    
                                                    {/* {topic.topic_status === 'approved' && (
                                                        <Tooltip title="Mark as Completed">
                                                            <IconButton onClick={() => handleOpenCompleteDialog(topic)} color="success" size="small">
                                                                <TaskAlt />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )} */}
                                                    
                                                    {topic.topic_status === 'revision_required' && (
                                                        <>
                                                            <Tooltip title="Approve After Revision">
                                                                <IconButton onClick={() => handleOpenApproveDialog(topic)} color="success" size="small">
                                                                    <Verified />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reject">
                                                                <IconButton onClick={() => handleOpenRejectDialog(topic)} color="error" size="small">
                                                                    <CancelOutlined />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredTopics.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                        {filteredTopics.length === 0 && (
                            <Box sx={{ py: 8, textAlign: 'center' }}>
                                <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">No research topics found</Typography>
                                <Typography variant="body2" color="text.secondary">Topics submitted by your students will appear here</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* View Details Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
                >
                    <DialogTitle sx={{ 
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Research Topic Details</Typography>
                            <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedTopic && (
                            <Box sx={{ pt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                    <Chip label={selectedTopic.topic_id} color="primary" />
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {selectedTopic.university_name && (
                                            <Chip
                                                icon={<AccountBalance />}
                                                label={selectedTopic.university_name}
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        )}
                                        {selectedTopic.specialty_name && (
                                            <Chip
                                                icon={<Category />}
                                                label={selectedTopic.specialty_name}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        <Chip
                                            icon={getStatusIcon(selectedTopic.topic_status)}
                                            label={topicStatusOptions.find(s => s.id === selectedTopic.topic_status)?.name}
                                            color={getStatusColor(selectedTopic.topic_status)}
                                        />
                                    </Box>
                                </Box>

                                {/* Student Info */}
                                <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.grey[50] }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <School /> Student Information
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2"><strong>Name:</strong> {selectedTopic.student_name}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2"><strong>Registration ID:</strong> {selectedTopic.student_registration_id}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* University Information */}
                                {selectedTopic.university_name && (
                                    <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.grey[50] }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccountBalance /> University Information
                                        </Typography>
                                        <Typography variant="body2"><strong>University:</strong> {selectedTopic.university_name}</Typography>
                                    </Paper>
                                )}

                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Lightbulb color="primary" /> Topic Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Title:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.topic_title}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Description:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.topic_description}</Typography>
                                            </Grid>
                                            {selectedTopic.specialty_name && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2"><strong>Research Specialty:</strong></Typography>
                                                    <Chip 
                                                        icon={<Category />}
                                                        label={selectedTopic.specialty_name}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                    {selectedTopic.specialty_code && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            (Code: {selectedTopic.specialty_code})
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Science color="secondary" /> Research Details
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Keywords:</strong></Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                    {Array.isArray(selectedTopic.keywords) && selectedTopic.keywords.length > 0 ? (
                                                        selectedTopic.keywords.map((k, i) => (
                                                            <Chip key={i} label={k} size="small" />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No keywords provided</Typography>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Research Questions:</strong></Typography>
                                                {Array.isArray(selectedTopic.research_questions) && selectedTopic.research_questions.length > 0 ? (
                                                    <ul>
                                                        {selectedTopic.research_questions.map((q, i) => (
                                                            <li key={i}><Typography variant="body2">{q}</Typography></li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No research questions provided</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Objectives:</strong></Typography>
                                                {Array.isArray(selectedTopic.objectives) && selectedTopic.objectives.length > 0 ? (
                                                    <ul>
                                                        {selectedTopic.objectives.map((o, i) => (
                                                            <li key={i}><Typography variant="body2">{o}</Typography></li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No objectives provided</Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MenuBook color="info" /> Methodology & Contribution
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Methodology:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.methodology_brief || 'Not provided'}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Literature Review:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.literature_review_summary || 'Not provided'}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Expected Contribution:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.expected_contribution || 'Not provided'}</Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {selectedTopic.supervisor_feedback && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.warning.main }}>
                                                <Feedback /> Supervisor Feedback
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2">{selectedTopic.supervisor_feedback}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseViewDialog} variant="contained">Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Approve Dialog */}
                <Dialog open={openApproveDialog} onClose={handleCloseApproveDialog} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ bgcolor: theme.palette.success.main, color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle /> Approve Research Topic
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Topic:</strong> {selectedTopic?.topic_title}
                                </Typography>
                                {selectedTopic?.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Specialty:</strong> {selectedTopic.specialty_name}
                                    </Typography>
                                )}
                            </Alert>
                            <TextField
                                fullWidth
                                label="Approval Notes (Optional)"
                                multiline
                                rows={4}
                                value={approvalNotes}
                                onChange={(e) => setApprovalNotes(e.target.value)}
                                placeholder="Add any additional notes or comments..."
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseApproveDialog}>Cancel</Button>
                        <Button 
                            onClick={() => handleUpdateStatus(selectedTopic?.id, 'approved', approvalNotes)}
                            variant="contained" 
                            color="success"
                            disabled={statusUpdateLoading}
                        >
                            {statusUpdateLoading ? <CircularProgress size={24} /> : 'Approve Topic'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Reject Dialog */}
                <Dialog open={openRejectDialog} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Cancel /> Reject Research Topic
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Topic:</strong> {selectedTopic?.topic_title}
                                </Typography>
                                {selectedTopic?.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Specialty:</strong> {selectedTopic.specialty_name}
                                    </Typography>
                                )}
                            </Alert>
                            <TextField
                                fullWidth
                                label="Rejection Reason *"
                                multiline
                                rows={4}
                                value={feedbackInput}
                                onChange={(e) => setFeedbackInput(e.target.value)}
                                placeholder="Provide detailed reason for rejection..."
                                required
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseRejectDialog}>Cancel</Button>
                        <Button 
                            onClick={() => handleUpdateStatus(selectedTopic?.id, 'rejected', feedbackInput)}
                            variant="contained" 
                            color="error"
                            disabled={statusUpdateLoading || !feedbackInput.trim()}
                        >
                            {statusUpdateLoading ? <CircularProgress size={24} /> : 'Reject Topic'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Complete Dialog */}
                <Dialog open={openCompleteDialog} onClose={handleCloseCompleteDialog} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ bgcolor: theme.palette.success.main, color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TaskAlt /> Complete Research Topic
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Topic:</strong> {selectedTopic?.topic_title}
                                </Typography>
                                {selectedTopic?.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Specialty:</strong> {selectedTopic.specialty_name}
                                    </Typography>
                                )}
                            </Alert>
                            <TextField
                                fullWidth
                                label="Completion Notes (Optional)"
                                multiline
                                rows={4}
                                value={completionNotes}
                                onChange={(e) => setCompletionNotes(e.target.value)}
                                placeholder="Add completion remarks or final notes..."
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseCompleteDialog}>Cancel</Button>
                        <Button 
                            onClick={() => handleCompletedResearch(selectedTopic?.id, 'completed', completionNotes)}
                            variant="contained" 
                            color="success"
                            disabled={statusUpdateLoading}
                        >
                            {statusUpdateLoading ? <CircularProgress size={24} /> : 'Mark as Completed'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Request Revision Dialog */}
                <Dialog open={openFeedbackDialog} onClose={handleCloseFeedbackDialog} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ bgcolor: theme.palette.warning.main, color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RateReview /> Request Revision
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Topic:</strong> {selectedTopic?.topic_title}
                                </Typography>
                                {selectedTopic?.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Specialty:</strong> {selectedTopic.specialty_name}
                                    </Typography>
                                )}
                            </Alert>
                            <TextField
                                fullWidth
                                label="Revision Requirements *"
                                multiline
                                rows={5}
                                value={feedbackInput}
                                onChange={(e) => setFeedbackInput(e.target.value)}
                                placeholder="Provide detailed feedback and revision requirements..."
                                required
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseFeedbackDialog}>Cancel</Button>
                        <Button 
                            onClick={() => handleUpdateStatus(selectedTopic?.id, 'revision_required', feedbackInput)}
                            variant="contained" 
                            color="warning"
                            disabled={statusUpdateLoading || !feedbackInput.trim()}
                        >
                            {statusUpdateLoading ? <CircularProgress size={24} /> : 'Request Revision'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Filter Options Dialog - Now doesn't close on backdrop click */}
                <Dialog 
                    open={filterOptions} 
                    onClose={(event, reason) => {
                        // Only close if clicking the close button, not on backdrop click
                        if (reason === 'backdropClick') {
                            return;
                        }
                    }}
                    maxWidth="sm" 
                    fullWidth
                >
                    <DialogTitle>Filter Options</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Topic Status</InputLabel>
                                <Select
                                    name="topic_status"
                                    value={filters.topic_status}
                                    onChange={handleFilterChange}
                                    label="Topic Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    {topicStatusOptions.map((status) => (
                                        <MenuItem key={status.id} value={status.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {status.icon} {status.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <TextField
                                fullWidth
                                label="Student Name"
                                name="student_name"
                                value={filters.student_name}
                                onChange={handleFilterChange}
                                size="small"
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Research Specialty</InputLabel>
                                <Select
                                    name="specialty_id"
                                    value={filters.specialty_id}
                                    onChange={handleFilterChange}
                                    label="Research Specialty"
                                >
                                    <MenuItem value="">All Specialties</MenuItem>
                                    {getUniqueSpecialties().map((specialty) => (
                                        <MenuItem key={specialty.id} value={specialty.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Category />
                                                {specialty.name}
                                                {specialty.code && ` (${specialty.code})`}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>University</InputLabel>
                                <Select
                                    name="university_id"
                                    value={filters.university_id}
                                    onChange={handleFilterChange}
                                    label="University"
                                >
                                    <MenuItem value="">All Universities</MenuItem>
                                    {getUniqueUniversities().map((university) => (
                                        <MenuItem key={university.id} value={university.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccountBalance />
                                                {university.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <TextField
                                fullWidth
                                label="From Date"
                                type="date"
                                name="dateFrom"
                                value={filters.dateFrom}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                            <TextField
                                fullWidth
                                label="To Date"
                                type="date"
                                name="dateTo"
                                value={filters.dateTo}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 2 }}>
                        <Button 
                            onClick={clearFilters} 
                            variant="outlined" 
                            color="error"
                            startIcon={<ClearAll />}
                        >
                            Clear All
                        </Button>
                        <Button 
                            onClick={() => setFilterOptions(false)} 
                            variant="contained"
                            startIcon={<FilterIcon />}
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default SupervisorTopicView;

